export interface Rule
  extends RuleValueOneOf,
    RuleRequirementsOneOf,
    RuleConditionsOneOf,
    RuleConditionTypeOptionsOneOf,
    RuleTaxesOneOf {
  /**
   * Fixed fee. Must hold a positive value.
   * @deprecated Fixed fee. Must hold a positive value.
   * @replacedBy fixed_fee
   * @targetRemovalDate 2024-02-15
   */
  amount?: CommonMoney;
  /**
   * Percentage fee. For example, `5` represents a 5% fee applied to the order's total.
   * @decimalValue options { maxScale:2, gte:0, lte:100 }
   * @deprecated
   * @replacedBy percentage_fee
   * @targetRemovalDate 2024-02-15
   */
  percentage?: string | null;
  /** Fixed fee. Must hold a positive value. */
  fixedFee?: Money;
  /**
   * Percentage fee. For example, `5` represents a 5% fee applied to the order's total.
   * @decimalValue options { maxScale:2, gte:0, lte:100 }
   */
  percentageFee?: string | null;
  /**
   * Single condition that must be met for the rule to be applied to an order.
   * @deprecated Single condition that must be met for the rule to be applied to an order.
   * @replacedBy condition_options
   * @targetRemovalDate 2024-02-15
   */
  condition?: Condition;
  /**
   * Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.
   * @deprecated Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.
   * @replacedBy condition_tree_options
   * @targetRemovalDate 2024-02-15
   */
  conditionTree?: ConditionTree;
  /** Single condition that must be met for the rule to be applied to an order. */
  conditionOptions?: Condition;
  /** Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order. */
  conditionTreeOptions?: ConditionTree;
  /** Percentage value to apply as a custom tax rate. Range: [0-100]. */
  customTaxRate?: string | null;
  /**
   * Tax group ID. Internal only.
   * @format GUID
   */
  taxGroupId?: string | null;
  /**
   * Rule ID.
   * @readonly
   */
  id?: string | null;
  /**
   * Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the rule.
   * @format GUID
   */
  locationId?: string | null;
  /**
   * Rule name.
   * @maxLength 50
   * @minLength 1
   */
  name?: string | null;
  /**
   * Date and time the rule was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the rule was updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Percentage value to apply as a custom tax rate. For example, `5` represents a 5% fee applied to the order's total. <br />
   * Min: '0'. <br />
   * Max: `100`.
   * @decimalValue options { maxScale:2, gte:0, lte:100 }
   * @deprecated
   * @replacedBy taxes
   * @targetRemovalDate 2023-09-16
   */
  taxRate?: string | null;
  /**
   * Specifies the type of condition.
   * @deprecated Specifies the type of condition.
   * @replacedBy condition_type
   * @targetRemovalDate 2024-02-15
   */
  conditionsType?: ConditionsTypeWithLiterals;
  /** Specifies the type of condition. */
  conditionType?: ConditionTypeWithLiterals;
  /** Whether the rule is enabled. If `true`, the rule is evaluated when service fees are calculated. If `false`, the rule will not be evaluated when service fees are calculated. */
  enabled?: boolean | null;
  /**
   * Revision number. Increments by 1 each time the rule is updated.
   * To prevent conflicting changes, the existing `revision` must be used when updating a rule.
   * @readonly
   */
  revision?: string | null;
  /**
   * DEPRECATED. Defines the app that the rule is connected to.
   * @maxLength 100
   * @minLength 1
   * @deprecated DEPRECATED. Defines the app that the rule is connected to.
   * @replacedBy app_id
   * @targetRemovalDate 2023-02-19
   */
  label?: string | null;
  /**
   * Defines the app that the rule is connected to.
   * @format GUID
   */
  appId?: string | null;
  /** Rounding strategy to apply to fee and tax calculation. */
  roundingStrategy?: RoundingStrategyWithLiterals;
  /** Data Extensions */
  extendedFields?: ExtendedFields;
  /** Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of rules. */
  tags?: Tags;
}

/** @oneof */
export interface RuleValueOneOf {
  /**
   * Fixed fee. Must hold a positive value.
   * @deprecated Fixed fee. Must hold a positive value.
   * @replacedBy fixed_fee
   * @targetRemovalDate 2024-02-15
   */
  amount?: CommonMoney;
  /**
   * Percentage fee. For example, `5` represents a 5% fee applied to the order's total.
   * @decimalValue options { maxScale:2, gte:0, lte:100 }
   * @deprecated
   * @replacedBy percentage_fee
   * @targetRemovalDate 2024-02-15
   */
  percentage?: string | null;
}

/** @oneof */
export interface RuleRequirementsOneOf {
  /** Fixed fee. Must hold a positive value. */
  fixedFee?: Money;
  /**
   * Percentage fee. For example, `5` represents a 5% fee applied to the order's total.
   * @decimalValue options { maxScale:2, gte:0, lte:100 }
   */
  percentageFee?: string | null;
}

/** @oneof */
export interface RuleConditionsOneOf {
  /**
   * Single condition that must be met for the rule to be applied to an order.
   * @deprecated Single condition that must be met for the rule to be applied to an order.
   * @replacedBy condition_options
   * @targetRemovalDate 2024-02-15
   */
  condition?: Condition;
  /**
   * Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.
   * @deprecated Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order.
   * @replacedBy condition_tree_options
   * @targetRemovalDate 2024-02-15
   */
  conditionTree?: ConditionTree;
}

/** @oneof */
export interface RuleConditionTypeOptionsOneOf {
  /** Single condition that must be met for the rule to be applied to an order. */
  conditionOptions?: Condition;
  /** Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order. */
  conditionTreeOptions?: ConditionTree;
}

/** @oneof */
export interface RuleTaxesOneOf {
  /** Percentage value to apply as a custom tax rate. Range: [0-100]. */
  customTaxRate?: string | null;
  /**
   * Tax group ID. Internal only.
   * @format GUID
   */
  taxGroupId?: string | null;
}

/**
 * Money.
 * Default format to use. Sufficiently compliant with majority of standards: w3c, ISO 4217, ISO 20022, ISO 8583:2003.
 */
export interface CommonMoney {
  /**
   * Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.
   * @format DECIMAL_VALUE
   */
  value?: string;
  /**
   * Currency code. Must be valid ISO 4217 currency code (e.g., USD).
   * @format CURRENCY
   */
  currency?: string;
  /** Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative. */
  formattedValue?: string | null;
}

/**
 * Money.
 * Default format to use. Sufficiently compliant with majority of standards: w3c, ISO 4217, ISO 20022, ISO 8583:2003.
 */
export interface Money {
  /**
   * Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.
   * @format DECIMAL_VALUE
   */
  value?: string;
  /**
   * Currency code. Must be valid ISO 4217 currency code (e.g., USD).
   * @format CURRENCY
   */
  currency?: string;
  /** Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative. */
  formattedValue?: string | null;
}

export interface Condition extends ConditionValueOneOf {
  /**
   * Contains a numeric value and an operation.
   *
   * Required if `expectedFieldType` is `NUMBER`.
   */
  number?: _Number;
  /**
   * Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.
   *
   * Required if `expectedFieldType` is `LIST`.
   */
  list?: List;
  /**
   * Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.
   * @maxLength 1000
   * @deprecated
   * @replacedBy order_field_path
   * @targetRemovalDate 2024-02-15
   */
  path?: string;
  /**
   * Path of the field in the order entity that this condition will evaluate. For example, `priceSummary.subtotal`.
   * @maxLength 1000
   */
  orderFieldPath?: string;
  /**
   * Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).
   * @deprecated Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`).
   * @replacedBy expected_field_type
   * @targetRemovalDate 2024-02-15
   */
  expectedType?: ExpectedType;
  /** Type of the field specified in `orderFieldPath`. For example, the type of `priceSummary.subtotal` is `"NUMBER"`). */
  expectedFieldType?: ExpectedFieldTypeWithLiterals;
}

/** @oneof */
export interface ConditionValueOneOf {
  /**
   * Contains a numeric value and an operation.
   *
   * Required if `expectedFieldType` is `NUMBER`.
   */
  number?: _Number;
  /**
   * Contains an array of strings to compare with the value of the specified field. If the value of the field matches a string in the array, the condition is considered met.
   *
   * Required if `expectedFieldType` is `LIST`.
   */
  list?: List;
}

export interface ExpectedType {
  /** Type of the field specified in `orderFieldPath`. */
  value?: ValueWithLiterals;
}

export enum Value {
  /** Represents a number value. */
  NUMBER = 'NUMBER',
  /** Represents a string value. */
  STRING = 'STRING',
}

/** @enumType */
export type ValueWithLiterals = Value | 'NUMBER' | 'STRING';

export enum ExpectedFieldType {
  /** Unknown expected field type */
  UNKNOWN_EXPECTED_FIELD_TYPE = 'UNKNOWN_EXPECTED_FIELD_TYPE',
  /** Represents a number value. */
  NUMBER = 'NUMBER',
  /** Represents a string value. */
  STRING = 'STRING',
}

/** @enumType */
export type ExpectedFieldTypeWithLiterals =
  | ExpectedFieldType
  | 'UNKNOWN_EXPECTED_FIELD_TYPE'
  | 'NUMBER'
  | 'STRING';

export interface _Number {
  /** Numeric value to compare with the value of the specified field. */
  value?: number;
  /** Operation to use. */
  operation?: OperationWithLiterals;
}

export enum Operation {
  /** Equal to. */
  EQ = 'EQ',
  /** Strictly less than. */
  LT = 'LT',
  /** Less than or equal to. */
  LE = 'LE',
  /** Strictly greater than. */
  GT = 'GT',
  /** Greater than or equal to. */
  GE = 'GE',
}

/** @enumType */
export type OperationWithLiterals =
  | Operation
  | 'EQ'
  | 'LT'
  | 'LE'
  | 'GT'
  | 'GE';

export interface List {
  /**
   * Array of string values to compare with the value of the field. If the value of the fields matches a string in the array, the condition is considered met.
   * @minSize 1
   * @maxSize 100
   * @minLength 1
   * @maxLength 100
   */
  values?: string[];
}

/** Used to represent a logical condition in the form of a tree structure. */
export interface ConditionTree
  extends ConditionTreeLeftConditionNodeOneOf,
    ConditionTreeRightConditionNodeOneOf {
  /** Single condition that must be met for the rule to be applied to an order. */
  leftCondition?: Condition;
  /** Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order. */
  leftConditionsTree?: ConditionTree;
  /** Single condition that must be met for the rule to be applied to an order. */
  rightCondition?: Condition;
  /** Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order. */
  rightConditionsTree?: ConditionTree;
  /** Specifies the logical operator to use when combining the evaluation of the left and right conditions. */
  operator?: OperatorWithLiterals;
}

/** @oneof */
export interface ConditionTreeLeftConditionNodeOneOf {
  /** Single condition that must be met for the rule to be applied to an order. */
  leftCondition?: Condition;
  /** Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order. */
  leftConditionsTree?: ConditionTree;
}

/** @oneof */
export interface ConditionTreeRightConditionNodeOneOf {
  /** Single condition that must be met for the rule to be applied to an order. */
  rightCondition?: Condition;
  /** Binary tree of conditions. Use the operator to indicate whether only one or both conditions must be met in order for a service fee rule to be applied to an order. */
  rightConditionsTree?: ConditionTree;
}

export enum Operator {
  /** The condition is true if both the left and right sides are true. */
  AND = 'AND',
  /** The condition is true if either the left or right side is true. */
  OR = 'OR',
}

/** @enumType */
export type OperatorWithLiterals = Operator | 'AND' | 'OR';

export enum ConditionsType {
  /** Indicates that the Rule has no conditions specified. */
  NO_CONDITIONS = 'NO_CONDITIONS',
  /** Indicates that the Rule has a single Condition. */
  CONDITION = 'CONDITION',
  /** Indicates that the Rule has a complex condition specified as a ConditionTree. */
  CONDITION_TREE = 'CONDITION_TREE',
}

/** @enumType */
export type ConditionsTypeWithLiterals =
  | ConditionsType
  | 'NO_CONDITIONS'
  | 'CONDITION'
  | 'CONDITION_TREE';

export enum ConditionType {
  /** Indicates that the Rule has no conditions specified. */
  UNDEFINED_CONDITION_TYPE = 'UNDEFINED_CONDITION_TYPE',
  /** Indicates that the Rule has a single Condition. */
  CONDITION = 'CONDITION',
  /** Indicates that the Rule has a complex condition specified as a ConditionTree. */
  CONDITION_TREE = 'CONDITION_TREE',
}

/** @enumType */
export type ConditionTypeWithLiterals =
  | ConditionType
  | 'UNDEFINED_CONDITION_TYPE'
  | 'CONDITION'
  | 'CONDITION_TREE';

export enum RoundingStrategy {
  /** Rounds up any number exactly halfway between two integers. For example, `2.5` rounds to `3`, and `3.5` and rounds to `4`. */
  HALF_UP = 'HALF_UP',
  /** Rounds such numbers to the nearest even integer. For example, `2.5` rounds to `2`, but `3.5` rounds to `4`. */
  HALF_EVEN = 'HALF_EVEN',
}

/** @enumType */
export type RoundingStrategyWithLiterals =
  | RoundingStrategy
  | 'HALF_UP'
  | 'HALF_EVEN';

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

/**
 * Common object for tags.
 * Should be use as in this example:
 * message Foo {
 * string id = 1;
 * ...
 * Tags tags = 5
 * }
 * example of taggable entity
 * {
 * id: "123"
 * tags: {
 * tags: {
 * tag_ids:["11","22"]
 * },
 * private_tags: {
 * tag_ids: ["33", "44"]
 * }
 * }
 * }
 */
export interface Tags {
  /** Tags that require an additional permission in order to access them, normally not given to site members or visitors. */
  privateTags?: TagList;
  /** Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors. */
  tags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

export interface CalculateServiceFeesRequest {
  /** Order information needed to evaluate the rules and calculate the relevant fees. */
  order: Order;
  /**
   * DEPRECATED. Defines the app that the rule is connected to.
   * @maxLength 100
   * @minLength 1
   * @deprecated DEPRECATED. Defines the app that the rule is connected to.
   * @replacedBy app_id
   * @targetRemovalDate 2023-02-19
   */
  label?: string | null;
  /**
   * Defines the app that the rule is connected to.
   * @format GUID
   */
  appId?: string | null;
}

export interface Order {
  /**
   * Order ID.
   * @readonly
   */
  id?: string;
  /**
   * ID of the site's location.
   * @format GUID
   */
  locationId?: string | null;
  /**
   * Currency used for pricing on the site.
   * @format CURRENCY
   */
  currency?: string | null;
  /** Information about the price of the order. */
  priceSummary?: PriceSummary;
  /** Order's shipping information. */
  shippingInfo?: ShippingInformation;
  /** Platform on which the order was placed. */
  platform?: Platform;
  /** Order's locale. */
  locale?: Locale;
}

export interface PriceSummary {
  /**
   * Subtotal of the order.
   * @decimalValue options { maxScale:3 }
   */
  subtotal?: string;
}

export interface ShippingInformation {
  /** Information about the type of delivery. For example, pick-up. */
  logistics?: DeliveryLogistics;
}

export interface DeliveryLogistics {
  /** Type of delivery. For example, pick-up. */
  type?: TypeWithLiterals;
}

export enum Type {
  /** Missing type due to an error. */
  UNSPECIFIED_FULFILLMENT_TYPE = 'UNSPECIFIED_FULFILLMENT_TYPE',
  /** Pickup */
  PICKUP = 'PICKUP',
  /** Delivery */
  DELIVERY = 'DELIVERY',
  /** Dine-in */
  DINE_IN = 'DINE_IN',
  /** Curbside-pickup */
  CURBSIDE_PICKUP = 'CURBSIDE_PICKUP',
}

/** @enumType */
export type TypeWithLiterals =
  | Type
  | 'UNSPECIFIED_FULFILLMENT_TYPE'
  | 'PICKUP'
  | 'DELIVERY'
  | 'DINE_IN'
  | 'CURBSIDE_PICKUP';

export interface Platform {
  /** Platform on which the order was placed. */
  value?: PlatformValueWithLiterals;
}

export enum PlatformValue {
  /** Site */
  SITE = 'SITE',
  /** Mobile site */
  MOBILE_SITE = 'MOBILE_SITE',
  /** Mobile app */
  MOBILE_APP = 'MOBILE_APP',
}

/** @enumType */
export type PlatformValueWithLiterals =
  | PlatformValue
  | 'SITE'
  | 'MOBILE_SITE'
  | 'MOBILE_APP';

export interface Locale {
  /**
   * Locale in [IETF BCP 47 language tag](https://en.wikipedia.org/wiki/IETF_language_tag) format.
   * Typically, this is a lowercase 2-letter language code,
   * followed by a hyphen, followed by an uppercase 2-letter country code.
   * For example, `en-US` for U.S. English, and `de-DE` for Germany German.
   */
  languageCode?: string | null;
  /** 2-letter country code in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format. */
  country?: string | null;
}

export interface CalculateServiceFeesResponse {
  /** A list of calculated fees based on rules evaluation. */
  calculatedFees?: CalculatedFee[];
}

export interface CalculatedFee {
  /**
   * The ID of the rule that was used to calculate the fee.
   * @format GUID
   */
  ruleId?: string;
  /**
   * The name of the rule that was used to calculate the fee.
   * @readonly
   */
  name?: string;
  /** Fee amount. */
  fee?: Money;
  /** Tax amount. */
  tax?: Money;
  /**
   * Tax group ID. This is an alternative to calculating the tax amount manually.
   * @format GUID
   */
  taxGroupId?: string | null;
}

export interface CreateRuleRequest {
  /** Rule to create. */
  rule: Rule;
}

export interface CreateRuleResponse {
  /** Created rule */
  rule?: Rule;
}

export interface GetRuleRequest {
  /**
   * ID of the rule to retrieve.
   * @format GUID
   */
  ruleId: string;
}

export interface GetRuleResponse {
  /** The retrieved rule. */
  rule?: Rule;
}

export interface UpdateRuleRequest {
  /** Rule to update. */
  rule: Rule;
}

export interface UpdateRuleResponse {
  /** Updated rule. */
  rule?: Rule;
}

export interface DeleteRuleRequest {
  /**
   * ID of the rule to delete.
   * @format GUID
   */
  ruleId: string;
}

export interface DeleteRuleResponse {}

export interface ListRulesRequest {
  /**
   * Retrieve only rule that apply at the [location](https://dev.wix.com/docs/rest/api-reference/business-tools/locations/location-object) with this ID. If this field is `null`, the rules will not be filtered by location.
   * @format GUID
   */
  locationId?: string | null;
  /**
   * DEPRECATED. Defines the app that the rule is connected to.
   * @maxLength 100
   * @minLength 1
   * @deprecated DEPRECATED. Defines the app that the rule is connected to.
   * @replacedBy app_id
   * @targetRemovalDate 2023-02-19
   */
  label?: string | null;
  /**
   * Defines the app that the rule is connected to.
   * @format GUID
   */
  appId?: string | null;
}

export interface ListRulesResponse {
  /** Array containing the retrieved rules. */
  rules?: Rule[];
}

export interface QueryRulesRequest {
  /** Query options. */
  query: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   * See [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language)
   * for more information.
   */
  filter?: Record<string, any> | null;
  /** Sort object. */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Number of items to load. <br />
   * Default: `50`
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * You can get the relevant cursor token
   * from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   */
  cursor?: string | null;
}

export interface QueryRulesResponse {
  /** The retrieved rules. */
  rules?: Rule[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  cursors?: Cursors;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /** Cursor pointing to next page in the list of results. */
  next?: string | null;
  /** Cursor pointing to previous page in the list of results. */
  prev?: string | null;
}

export interface BulkCreateRulesRequest {
  /** Rules to create. */
  rules: Rule[];
  /**
   * Whether the full rule entity is returned. <br />
   * Default: `false`.
   */
  returnFullEntity?: boolean;
}

export interface BulkCreateRulesResponse {
  /** The created rules. Omitted if `returnFullEntity` is set to false. */
  results?: BulkRuleResult[];
  /** Bulk Create Rule metadata. */
  bulkActionMetadata?: CommonBulkActionMetadata;
}

export interface BulkRuleResult {
  /** Metadata of the rule. */
  itemMetadata?: CommonItemMetadata;
  /** The created rule. */
  rule?: Rule;
}

export interface CommonItemMetadata {
  /** Item ID. Should always be available, unless it's impossible (for example, when failing to create an item). */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface CommonBulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateRulesRequest {
  /** Masked rules to update. */
  rules: MaskedRule[];
  /**
   * Whether the full rule entity is returned. <br />
   * Default: `false`.
   */
  returnFullEntity?: boolean;
}

export interface MaskedRule {
  /** Rule to update. */
  rule?: Rule;
  /** Explicit list of fields to update */
  mask?: string[];
}

export interface BulkUpdateRulesResponse {
  /** The updated rules. Omitted if `returnFullEntity` is set to false. */
  results?: BulkRuleResult[];
  /** Bulk Update Rule metadata. */
  bulkActionMetadata?: CommonBulkActionMetadata;
}

export interface BulkDeleteRulesRequest {
  /**
   * IDs of the rules to delete.
   * @format GUID
   */
  ruleIds: string[];
}

export interface BulkDeleteRulesResponse {
  /** Information about the deleted rules. */
  results?: BulkRuleResult[];
  /** Bulk Delete Rule metadata. */
  bulkActionMetadata?: CommonBulkActionMetadata;
}

export interface BulkUpdateRuleTagsRequest {
  /**
   * IDs of the rules to update tags for.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  ruleIds: string[];
  /** Tags to assign to the rules. */
  assignTags?: Tags;
  /** Tags to unassign from the rules. */
  unassignTags?: Tags;
}

export interface BulkUpdateRuleTagsResponse {
  /**
   * Results of the bulk update.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateRuleTagsResult[];
  /** Metadata for the bulk update. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface BulkUpdateRuleTagsResult {
  /** Metadata for the updated rule. */
  itemMetadata?: ItemMetadata;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateRuleTagsByFilterRequest {
  /** Filter that determines which rules to update tags for. */
  filter: Record<string, any> | null;
  /** Tags to assign to the rules. */
  assignTags?: Tags;
  /** Tags to unassign from the rules. */
  unassignTags?: Tags;
}

export interface BulkUpdateRuleTagsByFilterResponse {
  /**
   * Job ID. Pass this ID to Get Async Job ([SDK](https://dev.wix.com/docs/sdk/backend-modules/async-jobs/get-async-job) | [REST](https://dev.wix.com/docs/rest/business-management/async-job/get-async-job)) to track the job's status.
   * @format GUID
   */
  jobId?: string;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface Empty {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type BulkUpdateRuleTagsApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkUpdateRuleTagsByFilterApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
